import { z } from 'zod';
import { TaskWithAssignments } from './assignment';

// Assignee identity is a 3-way exclusive union (user / member / agent account)
export const MergeAssigneeKeySchema = z.object({
  userId: z.string().uuid().optional(),
  memberId: z.string().uuid().optional(),
  agentAccountId: z.string().uuid().optional(),
});

export type MergeAssigneeKey = z.infer<typeof MergeAssigneeKeySchema>;

export const MergeResolutionModeSchema = z.enum(['PRIMARY', 'ALL', 'EXPLICIT']);

export type MergeResolutionMode = z.infer<typeof MergeResolutionModeSchema>;

// Resolved final values chosen in the merge dialog; an omitted key keeps the primary's current value
export const MergeTasksFieldsSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  dueDate: z.string().nullable().optional(),
  priority: z.string().optional(),
  estimatedHours: z.number().nullable().optional(),
  storyPoints: z.number().nullable().optional(),
});

export type MergeTasksFields = z.infer<typeof MergeTasksFieldsSchema>;

export const MergeTasksRequestSchema = z.object({
  primaryTaskId: z.string().uuid(),
  mergedTaskIds: z.array(z.string().uuid()).min(1),
  fields: MergeTasksFieldsSchema.optional(),
  assignees: z.object({
    mode: MergeResolutionModeSchema,
    keys: z.array(MergeAssigneeKeySchema).optional(),
  }),
  tags: z.object({
    mode: MergeResolutionModeSchema,
    tagIds: z.array(z.string().uuid()).optional(),
  }),
  include: z.object({
    comments: z.boolean(),
    attachments: z.boolean(),
    subtasks: z.boolean(),
    activity: z.boolean(),
    timeEntries: z.boolean(),
  }),
});

export type MergeTasksRequest = z.infer<typeof MergeTasksRequestSchema>;

export interface MergeTasksMovedCounts {
  comments: number;
  attachments: number;
  subtasks: number;
  timeEntries: number;
  dependencies: number;
  tags: number;
  activity: number;
}

export interface MergeTasksResponse {
  primaryTask: TaskWithAssignments;
  deletedTaskIds: string[];
  primaryThreadId: string | null;
  removedThreadIds: string[];
  moved: MergeTasksMovedCounts;
}

export interface MergeTaskPreviewAssignee {
  key: MergeAssigneeKey;
  name: string;
  avatarUrl?: string | null;
}

export interface MergeTaskPreviewTag {
  id: string;
  name: string;
  color: string | null;
}

export interface MergeTaskPreviewCounts {
  comments: number;
  attachments: number;
  subtaskTree: number;
  timeEntries: number;
  dependencies: number;
  customFields: number;
  activity: number;
}

export interface MergeTaskPreviewItem {
  id: string;
  name: string;
  taskNumber: string | null;
  description: string | null;
  status: string;
  statusTypeId: string | null;
  priority: string;
  dueDate: string | null;
  estimatedHours: number | null;
  storyPoints: number | null;
  isArchived: boolean;
  completedAt: string | null;
  isRecurringTemplate: boolean;
  moduleType: string | null;
  moduleId: string | null;
  moduleName: string | null;
  assignees: MergeTaskPreviewAssignee[];
  tags: MergeTaskPreviewTag[];
  counts: MergeTaskPreviewCounts;
}

export interface MergeTasksPreview {
  tasks: MergeTaskPreviewItem[];
}

export const MERGE_TASKS_MAX_SELECTION = 10;
